import config from '@config';
import { logout } from '@modules/auth';
import { store } from '@stores';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const baseConfig = config;

export const instance = axios.create({
  baseURL: baseConfig.API_URL //TODO: 배포 전 API_URL로 복구
});

// 요청이 전달되기 전에 작업 수행
instance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const isLogin = config.url === '/auth/login';
    const isRefresh = config.url === '/auth/refresh';
    const isRefreshSignedCookie = config.url === '/auth/refresh-signed-cookie';
    const isPostImageGenerate = config.url === '/ai/model-to-character' && config.method === 'post';

    if (isLogin || isRefreshSignedCookie) {
      config.withCredentials = true;
    } else {
      config.withCredentials = false;
    }

    config.headers['Accept'] = 'application/json';

    //login 및 refresh 이외의 api 요청일 시 헤더에 accessToken을 실어보냄
    if (!isLogin && !isRefresh && sessionStorage.getItem('accessToken')) {
      config.headers['Authorization'] = `Bearer ${sessionStorage.getItem('accessToken')}`;
    }

    if (isLogin) {
      delete config.headers['Authorization'];
    }

    //refresh api 요청일 시 헤더에 refreshToken을 실어보냄
    if (isRefresh) {
      delete config.headers['Authorization'];
      config.headers['refresh-token'] = `${sessionStorage.getItem('refreshToken')}`;
    }

    if (isPostImageGenerate) {
      config.headers['Content-Type'] = 'multipart/form-data';
    } else {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    // 요청 오류가 있는 작업 수행
    return Promise.reject(error);
  }
);

export interface IPage {
  page: number;
  take: number;
  itemCount: number;
  pageCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface FetchResult<T = any> {
  data: T;
  meta?: IPage;
  status?: number | string;
  errorMessage?: string;
}

export const handleRefreshToken = async () => {
  const data = await instance.get(`/auth/refresh`);
  return data;
};

export const handleInvalidToken = () => {
  //컴포넌트가 아닌 곳에서도 Redux 상태를 변경할 수 있도록 store를 직접 import
  store.dispatch(logout());
};

instance.interceptors.response.use(
  (response) => {
    // 2xx 범위에 있는 상태 코드는 이 함수를 트리거 합니다.
    // 응답 데이터가 있는 작업 수행
    return response;
  },
  async (error) => {
    // 2xx 외의 범위에 있는 상태 코드는 이 함수를 트리거 합니다.
    // 응답 오류가 있는 작업 수행
    const { response, config }: AxiosError = error;

    const isLogin = config && config.url === '/auth/login';
    const isRefresh = config && config.url === '/auth/refresh';

    if ((response?.status as number) === 401) {
      if (isLogin) {
        return Promise.reject(error);
      }

      // GET /auth/refresh 요청시 만료된 refresh token을 전달하면 401이 반환되므로, 로그아웃 처리하여 재로그인을 유도한다
      if (isRefresh) {
        handleInvalidToken();
        return Promise.reject(error);
      }

      await handleRefreshToken().then((res) => {
        if (res.data.data.accessToken) {
          sessionStorage.setItem('accessToken', res.data.data.accessToken);
        }
        if (res.data.data.refreshToken) {
          sessionStorage.setItem('refreshToken', res.data.data.refreshToken);
        }
      });

      if (config) {
        return instance(config);
      }
    }

    if ((response?.status as number) === 403) {
      handleInvalidToken();
      return Promise.reject(error);
    }

    if (![401, 403].includes(response?.status as number)) {
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);
